import axios from 'axios'
import { ElNotification , ElMessageBox, ElMessage, ElLoading } from 'element-plus'
import { getToken } from '@/modules/huyu-framework/backend/utils/auth'
import errorCode from '@/modules/huyu-framework/backend/utils/errorCode'
import { tansParams, blobValidate } from '@/modules/huyu-framework/backend/utils/ruoyi'
import { safeJsonParse } from '@/modules/huyu-framework/backend/utils/longId'
import cache from '@/modules/huyu-framework/backend/plugins/cache'
import { saveAs } from 'file-saver'
import useUserStore from '@/store/modules/user'

let downloadLoadingInstance;
// 是否显示重新登录
export let isRelogin = { show: false };

// 创建axios实例
const service = axios.create({
  // axios中请求配置有baseURL选项，表示请求URL公共部分
  baseURL: import.meta.env.VITE_APP_BASE_API,
  // 超时
  timeout: 10000,
  // 在反序列化阶段把超过 JS 安全整数范围的裸整数字面量（如 2085304488185540609）
  // 改写为带引号字符串，从根上避免 JS Number 精度丢失。实现见 utils/longId.js
  //
  // 注意：必须用普通函数，axios 通过 fn.call(config, ...) 传入 this=config，
  // 箭头函数拿不到 this，会丢失 responseType / transitional 守卫。
  // 本函数严格复刻 axios 原生 transformResponse 的判定条件，仅把 JSON.parse
  // 换成 safeJsonParse，不改变任何其它语义。
  transformResponse: [function transformResponse(data) {
    const transitional = this.transitional || axios.defaults.transitional || {}
    const JSONRequested = this.responseType === 'json'
    const shouldParse = (transitional.forcedJSONParsing && !this.responseType) || JSONRequested
    if (!shouldParse || typeof data !== 'string' || data.length === 0) {
      return data
    }
    try {
      return safeJsonParse(data)
    } catch (e) {
      // 区分两种失败：报文本身不是 JSON（axios 原生行为），
      // 还是 quoteUnsafeIntegers 改写破坏了合法 JSON（本层缺陷，必须暴露）。
      try {
        JSON.parse(data)
      } catch (rawError) {
        // 原文同样不是合法 JSON：遵循 axios 的 silentJSONParsing 语义
        if (!transitional.silentJSONParsing && JSONRequested) throw e
        return data
      }
      throw new Error('长整型改写破坏了合法 JSON 报文，请检查 longId.quoteUnsafeIntegers：' + e.message)
    }
  }]
})

// request拦截器
service.interceptors.request.use(config => {
  // 染色定向路由（X-Server-Node）：仅 dev 环境注入，生产构建自动剥离。
  // 默认 auto=本机 CrossMachine 扩展节点有该服务则打本机，否则回落线上 base。
  // 控制台可切换：localStorage.setItem('huyu.routePref','remote') 强制走线上；
  // 'self' 只打本机（本机没有则 503）。清掉该键即恢复 auto。
  if (import.meta.env.DEV) {
    let pref = 'auto'
    try { pref = localStorage.getItem('huyu.routePref') || 'auto' } catch (e) { /* ignore */ }
    if (['auto', 'remote', 'self'].includes(pref)) {
      config.headers['X-Server-Node'] = pref
    }
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  } else if (!config.headers?.keepContentType) {
    config.headers['Content-Type'] = 'application/json;charset=utf-8'
  }
  // 是否需要设置 token
  const isToken = (config.headers || {}).isToken === false
  // 是否需要防止数据重复提交
  const isRepeatSubmit = (config.headers || {}).repeatSubmit === false
  if (getToken() && !isToken) {
    config.headers['Authorization'] = 'Bearer ' + getToken() // 让每个请求携带自定义token 请根据实际情况自行修改
  }
  // get请求映射params参数
  if (config.method === 'get' && config.params) {
    let url = config.url + '?' + tansParams(config.params);
    url = url.slice(0, -1);
    config.params = {};
    config.url = url;
  }
  if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put') && !(config.data instanceof FormData)) {
    const requestObj = {
      url: config.url,
      data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
      time: new Date().getTime()
    }
    const requestSize = Object.keys(JSON.stringify(requestObj)).length; // 请求数据大小
    const limitSize = 5 * 1024 * 1024; // 限制存放数据5M
    if (requestSize >= limitSize) {
      console.warn(`[${config.url}]: ` + '请求数据大小超出允许的5M限制，无法进行防重复提交验证。')
      return config;
    }
    const sessionObj = cache.session.getJSON('sessionObj')
    if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
      cache.session.setJSON('sessionObj', requestObj)
    } else {
      const s_url = sessionObj.url;                // 请求地址
      const s_data = sessionObj.data;              // 请求数据
      const s_time = sessionObj.time;              // 请求时间
      const interval = 1000;                       // 间隔时间(ms)，小于此时间视为重复提交
      // console.log('对比',s_data, requestObj.data  , requestObj.time   ,s_time,interval );
      


      if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
        const message = '数据正在处理，请勿重复提交';
        console.warn(`[${s_url}]: ` + message)
        return Promise.reject(new Error(message))
      } else {
        cache.session.setJSON('sessionObj', requestObj)
      }
    }
  }
  return config
}, error => {
    console.log(error)
    Promise.reject(error)
})

// 清理防重复提交的sessionObj
function clearSessionObj(config) {
  if (!config) return;
  const isRepeatSubmit = (config.headers || {}).repeatSubmit === false;
  if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
    cache.session.remove('sessionObj');
  }
}

// response拦截器
service.interceptors.response.use(res => {
    // 请求完成后清理防重复提交标记
    clearSessionObj(res.config);

    // 是否需要 token（isToken === false 表示登录前请求，不弹登录过期 401 弹窗）
    const isTokenRequired = (res.config.headers || {}).isToken !== false
    // 是否静默错误（silentError === true 表示失败时不弹 ElMessage/ElNotification 全局提示，直接交给上层处理）
    const silentError = (res.config.headers || {}).silentError === true
    // 未设置状态码则默认成功状态
    const code = res.data.code || 200;
    // 获取错误信息
    const msg = errorCode[code] || res.data.msg || errorCode['default']
    // 二进制数据或流式响应则直接返回
    if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
      return res.data
    }
    // 流式响应返回完整的response对象
    if (res.request.responseType === 'stream') {
      return res
    }

 
    if (code === 401) {
      // 登录前接口（isToken=false）遇到 401 不弹登录过期框，直接抛出错误让上层降级处理
      if (!isTokenRequired) {
        return Promise.reject(new Error(msg || '401 Unauthorized'))
      }
      if (!isRelogin.show) {
        isRelogin.show = true;
        ElMessageBox.confirm('登录状态已过期，您可以继续留在该页面，或者重新登录', '系统提示', { confirmButtonText: '重新登录', cancelButtonText: '取消', type: 'warning' }).then(() => {
          isRelogin.show = false;
          useUserStore().logOut().then(() => {
            location.href = '/login';
          })
      }).catch(() => {
        isRelogin.show = false;
      });
    }
      return Promise.reject('无效的会话，或者会话已过期，请重新登录。')
    } else if (code === 500) {
      if (!silentError) {
        ElMessage({ message: msg, type: 'error' })
      }
      return Promise.reject(new Error(msg))
    } else if (code === 601) {
      if (!silentError) {
        ElMessage({ message: msg, type: 'warning' })
      }
      return Promise.reject(new Error(msg))
    } else if (code !== 200) {
      console.log(res)
      // 检查URL参数中是否包含NoVerification=true，如果包含则不提示错误
      const hasNoVerification = res.config && res.config.url && res.config.url.includes('noVerification=true');
      if (!silentError && !hasNoVerification) {
        ElNotification.error({ title: msg })
      }
      return Promise.reject('error')
    } else {
      return  Promise.resolve(res.data)
    }
  },
  error => {
    // 请求失败时也清理防重复提交标记
    if (error.config) {
      clearSessionObj(error.config);
    }
    
    // 是否静默错误（silentError === true 表示不弹全局提示，直接抛出让上层降级处理）
    const silentError = (error.config?.headers || {}).silentError === true
    
    console.log('err' + error)
    let { message } = error;
    if (message == "Network Error") {
      message = "后端接口连接异常";
    } else if (message.includes("timeout")) {
      message = "系统接口请求超时";
    } else if (message.includes("Request failed with status code")) {
      message = "系统接口" + message.substr(message.length - 3) + "异常";
    }
    if (!silentError) {
      ElMessage({ message: message, type: 'error', duration: 5 * 1000 })
    }
    return Promise.reject(error)
  }
)

// 通用下载方法
export function download(url, params, filename, config) {
  downloadLoadingInstance = ElLoading.service({ text: "正在下载数据，请稍候", background: "rgba(0, 0, 0, 0.7)", })
  const cfg = config || {}
  return service.post(url, params, {
    transformRequest: [(params) => { return tansParams(params) }],
    responseType: 'blob',
    // 导出必须覆盖实例默认 timeout(10s)：全量导出数据量大/服务端慢SQL时超过 10 秒，
    // axios 直接 abort 并走 catch 弹"下载文件出现错误"，而服务端其实仍在正常生成文件
    timeout: 120000,
    ...cfg,
    // headers 单独合并（不能被 cfg.headers 整体覆盖）：
    // keepContentType: 请求拦截器对非FormData请求会强制覆盖为application/json，
    //   会导致Tomcat不按form解析请求体，form参数(如导出选中的ids)后端全部收不到；
    // repeatSubmit:false: 导出是只读操作，关闭防重复提交拦截——否则1秒内同参数二次点击
    //   会在前端直接被拒（"数据正在处理"），请求根本不发往服务端、操作日志也无记录；
    // silentError: 错误提示统一由下方 catch 给出具体原因，避免拦截器+catch 双重弹窗。
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      keepContentType: true,
      repeatSubmit: false,
      silentError: true,
      ...(cfg.headers || {}),
    },
  }).then(async (data) => {
    const isBlob = blobValidate(data);
    if (isBlob) {
      const blob = new Blob([data])
      saveAs(blob, filename)
    } else {
      const resText = await data.text();
      const rspObj = JSON.parse(resText);
      const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
      ElMessage.error(errMsg);
    }
    downloadLoadingInstance.close();
  }).catch(async (r) => {
    console.error(r)
    // silentError 已抑制拦截器的笼统提示，这里按失败原因给出具体文案
    let msg = '下载文件出现错误，请联系管理员！'
    if (r && r.response && r.response.data instanceof Blob) {
      // 后端返回了 JSON 错误体（HTTP 非2xx时 axios 仍按 blob 收集），解析出真实原因
      try {
        const obj = JSON.parse(await r.response.data.text())
        msg = obj.msg || msg
      } catch (e) { /* 非JSON错误体，保留通用文案 */ }
    } else if (r && (r.code === 'ECONNABORTED' || (r.message || '').includes('timeout'))) {
      msg = '导出超时：数据量较大或服务繁忙，请稍后重试'
    } else if (r && r.message === 'Network Error') {
      msg = '后端接口连接异常，请确认服务已启动'
    }
    ElMessage.error(msg);
    downloadLoadingInstance.close();
  })
}

export default service
